/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'

import {
  SvgIcon,
  Button,
  ExpandableOverviewItem,
  IdentityNumberFinder,
  IdentityClientCardSmall,
  IdentityContactCardSmall,
  IdentityBankAccountCardSmall,
  IdentityInvestmentBalancesCardSmall,
  IdentityCellphoneInput,
  LoanApplications as ClientLoanApplications,
  CreditApplications as ClientCreditApplications,
  NavLink,
  ChooseTableRow,
  SpanWithData,
  Spinner,
  Table,
  Modal,
  Warning,
  Loans as ClientLoans,
  Credits as ClientCredits
} from '../'

import {
  decodeBase64,
  encodeBase64
} from '../../utils'

import {
  CONTENTTYPE_CREDITAPPLICATION,
  CONTENTTYPE_LOANAPPLICATION,
  CONTENTTYPE_REQUESTRUN,
  CONTENTTYPE_LOAN,
  CONTENTTYPE_CREDIT
} from '../../constants'

import {
  QUERY_FILERELATIONS
} from '../../queries'

import {
  MUTATION_FILERELATION,
  MUTATION_FILERELATION_DELETE
} from '../../mutations'

const QUERY_FILE = gql`
  query($id: ID!) {
    file(id: $id) {
      id
      parent {
        id
        asset
      }
    }
  }
`
const QUERY_FILE_SIBLINGS_AND_RELATIONS = gql`
  query($parentId: UUID!, $file: UUID!, $contentType: ID!) {
    files(parent_Id: $parentId) {
      edges {
        node {
          id
          asset
          hasRequestRuns
          requestruns {
            edges {
              node {
                id
                status
                requesttype {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
    filerelations(file_Id: $file, contentType_Id: $contentType, orderBy: "-created_at") {
      edges {
        node {
          id
          objectId
        }
      }
    }
  }
`

export class RequestAllocate extends React.PureComponent {
  static propTypes = {
    requestRun: PropTypes.object.isRequired,
    open: PropTypes.bool,
    updateRequestRun: PropTypes.func.isRequired,
    saveNewPrimaryMsisdn: PropTypes.func.isRequired,
    locked: PropTypes.bool,
    onClick: PropTypes.func,
    onCreateCaseClick: PropTypes.func.isRequired,
    refreshData: PropTypes.func,
    className: PropTypes.string,
    singleItemCondensedView: PropTypes.bool
  }

  static defaultProps = {
    open: false,
    requestRun: null
  }

  state = {
    allocateSuccess: false,
    reallocate: false,
    // fileRelationCreateObject: null,
    chooseRequestRunModal: false,
    linkToRequestRunInProgress: false
  }

  chooseRequestRunRef = React.createRef()

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (this.chooseRequestRunRef.current && !this.chooseRequestRunRef.current.contains(e.target)) {  // click is outside
      this.setState({ chooseRequestRunModal: false })
    }
  }

  updateRequestRun = (identity) => {
    let requestRun = this.props.requestRun
    // NOTE: should filerelation to identity also be created at this stage..?
    let input = {
      id: requestRun.id,
      identity
    }
    if (requestRun.isValidated) {
      input.validatedBy = 'null'
    }

    this.props.updateRequestRun(input)
    .then(() => {
      console.log('request run update successfully')
      this.setState({ allocateSuccess: true, reallocate: false })
    })
    .catch(error => console.log('Failed to update request run', error))
  }

  reallocateIdentity = () => {
    this.setState({ reallocate: true, allocateSuccess: false })
  }

  removeFileRelation = (filerelationDelete, fileRelation) => {
    filerelationDelete({
      variables: {
        input: {
          id: fileRelation
        }
      }
    })
    .then(({ data }) => {
      data.filerelationDelete.outcome
        ? console.log("File relation successfully removed.")
        : console.log("Removal of file relation not possible since invalid id was provided")
    })
    .catch((error) => {
      console.log("Removal of file relation failed", error)
    })
  }

  getAllocateToContentType = (contentTypeId) => (
    parseInt(decodeBase64(contentTypeId), 10)
  )

  openChooseRequestRunModal = () => this.setState({ chooseRequestRunModal: true })

  render() {
    let { requestRun, open, locked, onClick, onCreateCaseClick, singleItemCondensedView, refreshData, className } = this.props
    let identityAllocated = requestRun.identity
    let allocateToObject = requestRun.requesttype.allocateTo && !requestRun.isAllocated
    let allocateToContentType = requestRun.requesttype.allocateTo
      ? this.getAllocateToContentType(requestRun.requesttype.allocateTo.id)
      : null

    return (
      <ExpandableOverviewItem
        title='Allocate'
        summary={requestRun.identity
          ? `${requestRun.identity.firstName} ${requestRun.identity.lastName}`
          : ''
        }
        iconSymbol={identityAllocated ? 'check' : null}
        iconColour={identityAllocated ? 'success' : null}
        open={open}
        locked={locked}
        onClick={onClick}
        singleItemCondensedView={singleItemCondensedView}
        className={className}>
        {identityAllocated && !this.state.reallocate
          ? <div className='slds-m-bottom_medium slds-m-left_small'>
              {requestRun.identity.isContact && requestRun.identity.client === null
                ? <IdentityContactCardSmall identity={requestRun.identity.id} />
                : <IdentityClientCardSmall identity={requestRun.identity.id} />
              }
              <IdentityBankAccountCardSmall identity={requestRun.identity.id} />
              <IdentityInvestmentBalancesCardSmall identity={requestRun.identity.id} className='slds-m-bottom_x-large' />
              {((requestRun.workflowrunProcessing && requestRun.workflowrunProcessing.atStep === 2) || !requestRun.isCaptured) &&
                <Mutation mutation={MUTATION_FILERELATION_DELETE}>
                  {(filerelationDelete, { data }) => {
                    if (requestRun.requesttype.allocateTo && requestRun.isAllocated) {  // request run file has already been allocated to object
                      return (
                        <Query query={QUERY_FILERELATIONS} variables={{ file: decodeBase64(requestRun.sources[0].id), contentType: requestRun.requesttype.allocateTo.id }} >
                          {({ loading, error, data }) => {
                            if (loading || error) return null
                            if (data.filerelations.edges.length > 0) {
                              return (
                                <Button
                                  type={allocateToObject ? 'default' : 'brand'}
                                  minWidth={250}
                                  onClick={() => {
                                    this.removeFileRelation(filerelationDelete, data.filerelations.edges[0].node.id)
                                    this.reallocateIdentity()
                                  }}
                                  className='slds-m-bottom_small slds-m-bottom_large'>
                                  Reallocate to someone else
                                </Button>
                              )
                            } else {
                              return (
                                <Button
                                  type={allocateToObject ? 'default' : 'brand'}
                                  minWidth={250}
                                  onClick={this.reallocateIdentity}
                                  className='slds-m-bottom_small slds-m-bottom_large'>
                                  Reallocate to someone else
                                </Button>
                              )
                            }
                          }}
                        </Query>
                      )
                    } else {
                      return (
                        <Button
                          type={allocateToObject ? 'default' : 'brand'}
                          minWidth={250}
                          onClick={this.reallocateIdentity}
                          className='slds-m-bottom_small slds-m-bottom_large'>
                          Reallocate to someone else
                        </Button>
                      )
                    }
                  }}
                </Mutation>
              }
              {requestRun.requesttype.allocateTo &&
                <RequestAllocateTo
                  requestRun={requestRun}
                  allocateToContentType={allocateToContentType}
                  refreshData={() => {
                    this.props.updateRequestRun({ id: requestRun.id })  // saves request run to update update status from unallocated
                    refreshData()
                  }}
                />
              }
            </div>
          : <div id='allocateToParentRequestRunOrIdentity'>
              {requestRun.sources.length > 0 && requestRun.sources[0].filetype.multipage &&
                <Button onClick={this.openChooseRequestRunModal} minWidth={150} className='slds-m-bottom_medium'>
                  Secondary page
                </Button>
              }
              <IdentityNumberFinder
                onCreateCaseClick={onCreateCaseClick}
                numberFoundAdditionalRender={identity => (
                  <IdentityCellphoneInput
                    title={
                      <div className='u-font-weight-bold u-font-size-larger slds-m-bottom_x-small'>
                        Cellphone number
                      </div>
                    }
                    identity={identity.id}
                    initialAutoPopulate={true}
                    render={(number, feedback, addressId, isPrimaryNumber) => (
                      <div className='slds-m-top_large slds-m-bottom_x-large'>
                        <Button
                          type='brand'
                          onClick={() => {
                            if (!isPrimaryNumber && number.length > 0) {
                              this.props.saveNewPrimaryMsisdn(feedback === 'notPrimary'
                                ? addressId
                                : null, number, identity.id
                              )
                            }
                            this.updateRequestRun(identity.id)
                          }}
                          minWidth={180}
                          className='slds-m-right_small'
                          disabled={number.length > 0 && [ 'tooShort', 'tooLong' ].indexOf(feedback) > -1}>
                          Confirm
                        </Button>
                        or
                        <Button
                          type='brand'
                          onClick={this.props.onCreateCaseClick}
                          minWidth={150}
                          marginLeft='0.75rem'>
                          Create a case
                        </Button>
                      </div>
                    )}
                  />
                )}
                className='slds-m-left_medium'
              />
            </div>
        }
        {this.state.chooseRequestRunModal &&
          <Modal
            ref={this.chooseRequestRunRef}
            elementId='allocateToParentRequestRunOrIdentity'
            body={
              <FileSiblingRequestRuns
                file={requestRun.sources[0].id}
                requestType={requestRun.requesttype.id}
                onLinkToRequestRunSuccess={() => this.setState({ chooseRequestRunModal: false })}
              />
            }
          />
        }
      </ExpandableOverviewItem>
    )
  }
}

class FileSiblingRequestRuns extends React.Component {
  static propTypes = {
    file: PropTypes.string.isRequired,
    requestType: PropTypes.string.isRequired,
    onLinkToRequestRunSuccess: PropTypes.func.isRequired
  }

  state = {
    requestRunSelected: null
  }

  getFileRequestRuns = (files, requestType, fileRelations) => {
    let requestRuns = []

    files.forEach(file => file.node.requestruns.edges.forEach(requestRun => {
      if (requestRun.node.requesttype.id === requestType &&   // we only want to display files with request runs of same request type
        !fileRelations.find(relation => relation.objectId === decodeBase64(requestRun.node.id))) {  // and that hasn't been linked to yet
        let fileName = file.node.asset.split('/').pop()
        requestRuns.push(
          <tr onClick={() => this.setState({ requestRunSelected: requestRun.node.id })}>
            <td className='slds-scrollable_x' title={file.node.asset}>{fileName}</td>
            <td>{requestRun.node.requesttype.name}</td>
            <td><SpanWithData forField='RequestRunStatus' searchKey={requestRun.node.status} /></td>
          </tr>
        )
      }
    }))

    return requestRuns
  }

  linkToRequestRun = (filerelationMutate) => {
    filerelationMutate({
      variables: {
        input: {
          file: this.props.file,
          objectId: decodeBase64(this.state.requestRunSelected),
          contentType: CONTENTTYPE_REQUESTRUN
        }
      }
    })
    .then(() => this.setState({ linkToRequestRunInProgress: false, requestRunSelected: null }))
    .catch(() => this.setState({ linkToRequestRunInProgress: false, requestRunSelected: null }))
  }

  render() {
    return (
      <Query query={QUERY_FILE} variables={{ id: this.props.file }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Spinner className='slds-is-relative' size='x-small' />
          }
          if (error) {
            return <Warning>Failed to load file data.</Warning>
          }
          if (data.file.parent === null) {
            return  (
              <div>
                <SvgIcon variant='utility' symbol='info' colour='light' size='small' className='slds-m-right_small' />
                No parent files found.
              </div>
            )
          } else {
            let parentFile = data.file.parent
            return (
              <Query
                query={QUERY_FILE_SIBLINGS_AND_RELATIONS}
                variables={{
                  parentId: decodeBase64(parentFile.id),
                  file: decodeBase64(this.props.file),
                  contentType: encodeBase64(CONTENTTYPE_REQUESTRUN, "ContentTypeNode")
                }}>
                {({ loading, error, data}) => {
                  if (loading || error) {
                    if (error) console.log('error', error)
                    return null
                  }
                  let files = data.files.edges.filter(file => file.node.hasRequestRuns && file.node.id !== this.props.file)
                  // NOTE: probably need to filter out existing
                  if (files.length > 0) {
                    let fileRequestRuns = this.getFileRequestRuns(files, this.props.requestType, data.filerelations.edges.map(relation => relation.node))
                    if (fileRequestRuns.length > 0) {
                      return (
                        <div className='slds-size_1-of-1'>
                          <div className='u-font-size-larger u-font-weight-bold slds-m-bottom_medium'>
                            {`Other files with request runs also belonging to ${parentFile.asset.split('/').pop()}:`}
                          </div>
                          <ChooseTableRow>
                            <Table
                              columns={[ 'File', 'Request run', 'Request run status' ]}
                              rows={fileRequestRuns}
                              objectRows={true}
                              noCellPadding={true}
                            />
                          </ChooseTableRow>
                          <Mutation mutation={MUTATION_FILERELATION}>
                            {(filerelationMutate, { data }) => {
                              if (data && data.filerelationMutate) {
                                this.props.onLinkToRequestRunSuccess()
                              }
                              return (
                                <div className='slds-m-top_large slds-align_absolute-center'>
                                  <Button
                                    type='brand'
                                    disabled={this.state.requestRunSelected === null || this.state.linkToRequestRunInProgress}
                                    onClick={() => {
                                      this.setState({ linkToRequestRunInProgress: true })
                                      this.linkToRequestRun(filerelationMutate)
                                    }}
                                    minWidth={350}>
                                    {this.state.linkToRequestRunInProgress
                                      ? 'Linking...'
                                      : 'Link to selected request'
                                    }
                                  </Button>
                                  {data && data.error &&
                                    <Warning>Failed to link file to request.</Warning>
                                  }
                                </div>
                              )
                            }}
                          </Mutation>
                        </div>
                      )
                    } else {
                      return (
                        <div>
                          <SvgIcon variant='utility' symbol='info' colour='light' size='small' className='slds-m-right_small' />
                          No requests found to link to. Process initial page first.
                        </div>
                      )
                    }
                  } else {
                    return (
                      <div>
                        <SvgIcon variant='utility' symbol='info' colour='light' size='small' className='slds-m-right_small' />
                        No related pages found.
                      </div>
                    )
                  }
                }}
              </Query>
            )
          }
        }}
      </Query>
    )
  }
}

const Success = ({ message }) => (
  <div className='slds-grid slds-m-bottom_medium'>
    <div>
      <SvgIcon
        variant='utility'
        symbol='check'
        size='small'
        colour='success'
        className='slds-m-right_medium'
      />
    </div>
    <div className='u-font-size-large'>
      {message}
    </div>
  </div>
)

class RequestAllocateTo extends React.Component {
  static propTypes = {
    requestRun: PropTypes.object.isRequired,
    allocateToContentType: PropTypes.string.isRequired,
    refreshData: PropTypes.func
  }

  state = {
    fileRelationCreateObject: null,
    linkingInProgress: false
  }

  createFileRelationToObject = (filerelationMutate, contentType) => {
    filerelationMutate({
      variables: {
        input: {
          file: this.props.requestRun.sources[0].id,
          contentType,
          objectId: decodeBase64(this.state.fileRelationCreateObject)
        }
      }
    })
    .then(() => {
      this.setState({ linkingInProgress: false, allocateSuccess: true, reallocate: false }, () => (
        setTimeout(this.props.refreshData, 4000)
      ))
    })
    .catch(error => {
      Sentry.captureMessage(`Failed to create file relation - ${error}`)
      this.setState({ linkingInProgress: false })
    })
  }

  confirmObjectChoice = (object) => {
    this.setState({ fileRelationCreateObject: object })
  }

  clearObjectChoice = () => {
    this.setState({ fileRelationCreateObject: null })
  }

  hasFileRelationObjectBeenSelected = () => (
    this.state.fileRelationCreateObject !== null
  )

  getListingViewUrl = (objectId, allocateToContentType, identityId) => {
    let url = null
    let node = null
    switch (allocateToContentType) {
      case CONTENTTYPE_CREDITAPPLICATION:
        node = 'CreditApplicationNode'
        url = `/os/products/credit/applications/${encodeBase64(objectId, node)}`
        break

      case CONTENTTYPE_LOANAPPLICATION:
        node = 'LoanApplicationNode'
        url = `/os/products/credit/applications/${encodeBase64(objectId, node)}`
        break

      case CONTENTTYPE_LOAN:
        node = 'LoanNode'
        url = `os/identity/${identityId}/products/credit/${encodeBase64(objectId, node)}`
        break

      case CONTENTTYPE_CREDIT:
        node = 'CreditNode'
        url = `os/identity/${identityId}/products/credit/${encodeBase64(objectId, node)}`
        break

      default:
        break
    }

    return url
  }

  getAllocateToObjectType = () => {
    let objectTypeString = null

    switch (this.props.allocateToContentType) {
      case CONTENTTYPE_LOANAPPLICATION:
        objectTypeString = 'loan application'
        break

        case CONTENTTYPE_CREDITAPPLICATION:
          objectTypeString = 'credit application'
          break

      case CONTENTTYPE_LOAN:
        objectTypeString = 'loan'
        break

      case CONTENTTYPE_CREDIT:
        objectTypeString = 'loan'
        break

      default:
        break
    }

    return objectTypeString
  }

  showClientAssociatedObjects = (client) => (
    {
      [CONTENTTYPE_CREDITAPPLICATION]:
        <ClientCreditApplications
          client={client}
          onClick={this.confirmObjectChoice}
          orderBy='-created_at'
          className='u-font-size-smaller slds-scrollable_x'
        />,
      [CONTENTTYPE_LOANAPPLICATION]:
        <ClientLoanApplications
          client={client}
          onClick={this.confirmObjectChoice}
          orderBy='-created_at'
          className='u-font-size-smaller slds-scrollable_x'
        />,
      [CONTENTTYPE_LOAN]:
        <ClientLoans
          client={client}
          condensedView={true}
          onClick={this.confirmObjectChoice}
          orderBy='-created_at'
          className='u-font-size-smaller slds-m-top_small slds-m-bottom_small'
        />,
      [CONTENTTYPE_CREDIT]:
        <ClientCredits
          client={client}
          condensedView={true}
          onClick={this.confirmObjectChoice}
          orderBy='-created_at'
          className='u-font-size-smaller slds-m-top_small slds-m-bottom_small'
        />
    }[this.props.allocateToContentType.toString()]
  )

  isSupportedObjectType = () => (
    [CONTENTTYPE_CREDITAPPLICATION, CONTENTTYPE_CREDIT, CONTENTTYPE_LOANAPPLICATION, CONTENTTYPE_LOAN].indexOf(this.props.allocateToContentType) > -1
  )

  render() {
    let { requestRun, allocateToContentType } = this.props
    let allocateToObjectType = this.getAllocateToObjectType()

    return (
      <React.Fragment>
        {requestRun.isAllocated
          ? requestRun.requesttype.allocateTo
              ? <div className='slds-m-bottom_large'>
                  <Query query={QUERY_FILERELATIONS} variables={{ file: decodeBase64(requestRun.sources[0].id), contentType: requestRun.requesttype.allocateTo.id }} >
                    {({ loading, error, data }) => {
                      if (loading || error) return null
                      if (data.filerelations.edges.length > 0) {
                        return (
                          <Success
                            message={
                              <div>
                                File successfully allocated to <NavLink to={this.getListingViewUrl(data.filerelations.edges[0].node.objectId, allocateToContentType, requestRun.identity.id)}>{allocateToObjectType}</NavLink>
                              </div>
                            }
                          />
                        )
                      } else {
                        return null
                      }
                    }}
                  </Query>
                  <Success message={`${requestRun.requesttype.name} form successfully allocated`} />
                </div>
              : requestRun.isAllocated && <Success message={`${requestRun.requesttype.name} form successfully allocated`} />
          : this.isSupportedObjectType()
            ? <Mutation mutation={MUTATION_FILERELATION}>
                {(filerelationMutate, { data }) => {
                  if (data) {
                    return (
                      <React.Fragment>
                        <Success
                          message={
                            <div>
                              File successfully allocated to <NavLink to={this.getListingViewUrl(data.filerelationMutate.filerelation.objectId, allocateToContentType, requestRun.identity.id)}>{allocateToObjectType}</NavLink>
                            </div>
                          }
                        />
                        <Success message={`${requestRun.requesttype.name} form successfully allocated`} />
                      </React.Fragment>
                    )
                  }
                  return (
                    <div className='slds-m-top_medium slds-m-bottom_large'>
                      <div className='u-font-size-large u-font-weight-bold slds-m-bottom_small'>
                        Complete allocation by choosing which to link file to:
                      </div>
                      <ChooseTableRow>
                        {this.showClientAssociatedObjects(requestRun.identity.client.id)}
                      </ChooseTableRow>
                      <Button
                        type='brand'
                        minWidth={250}
                        disabled={!this.hasFileRelationObjectBeenSelected() || this.state.linkingInProgress}
                        onClick={() => {
                          this.setState({ linkingInProgress: true }, () => (
                            this.createFileRelationToObject(filerelationMutate, allocateToContentType)
                          ))
                        }}>
                        {`Link file to ${allocateToObjectType}`}
                      </Button>
                    </div>
                  )
                }}
              </Mutation>
            : <Warning>
                Assigned allocate to object type not currently supported.
              </Warning>
        }
      </React.Fragment>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/request-allocate/index.js